import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import * as React from "react";
import { BaseRow, Columns, UseMakeDataGridProps } from "./types";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { ColumnHeaderWithTooltip, EllipsisTooltipCell } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Tables";
/**
 * Pre-processes DataGrid columns to apply consistent behavior across all cells and headers.
 *
 * Features applied to all columns:
 * - **Column Header Tooltips**: All column headers automatically show a tooltip when the header text is truncated,
 *   using the same custom Tooltip component as the cells for consistent styling.
 * - **Text Truncation with Ellipsis**: By default, all cell content is truncated with "..." when it overflows,
 *   and a tooltip shows the full content on hover. This improves table readability and handles long text gracefully.
 * - **Opt-out Option**: Columns can disable truncation by setting `truncate: false` in the column definition.
 *   This is useful for columns containing tags, chips, or other content that should display at full width.
 * - **Filterable/Sortable Logic**: Configures filtering and sorting based on table mode (server/client).
 *
 * @param config - Configuration object with table mode settings
 * @param config.tableMode - Whether the table operates in "server" or "client" mode
 * @returns A function that processes column definitions and wraps renderCell with EllipsisTooltipCell
 *
 * @example
 * // Default behavior: truncation enabled for cells, tooltips on headers
 * const columns = [
 *   { field: 'name', headerName: 'Name' }, // Will be truncated with tooltip
 * ];
 *
 * @example
 * // Opt-out of truncation for specific columns
 * const columns = [
 *   { field: 'tags', headerName: 'Tags', truncate: false, renderCell: (params) => <GroupOfTags tags={params.value} /> },
 * ];
 */
export const makeColumnsPre =
  <R extends BaseRow, E extends keyof EntityMappings>({ tableMode = "server" }: UseMakeDataGridProps<R, E>) =>
  (columns: Columns<R, E>, depedancyArray?: unknown[]) => {
    return React.useMemo(() => {
      return columns.map((col) => {
        const filterable = tableMode === "server" && !col.remoteFilter ? false : col.filterable ?? true;
        const sortable = tableMode === "server" && !col.remoteOrder ? false : col.sortable ?? true;

        const headerClassName = "MuiDataGrid-columnHeaderTitle";

        // Wrap renderCell with EllipsisTooltipCell if column supports it
        let wrappedColumn = { ...col, filterable, sortable, headerClassName };

        // Add custom header renderer with tooltip
        wrappedColumn = {
          ...wrappedColumn,
          renderHeader: (params) => (
            <ColumnHeaderWithTooltip customRenderHeader={col?.renderHeader?.(params)} {...params} />
          ),
        } as typeof wrappedColumn;

        // Determine if truncation should be disabled for this column
        const shouldTruncate = col.truncate !== false; // Default to true

        // Check if this column type supports renderCell
        if ("renderCell" in col) {
          const originalRenderCell = col.renderCell;
          wrappedColumn = {
            ...wrappedColumn,
            renderCell: originalRenderCell
              ? (params: GridRenderCellParams) => (
                  <EllipsisTooltipCell truncate={shouldTruncate}>{originalRenderCell(params)}</EllipsisTooltipCell>
                )
              : (params: GridRenderCellParams) => (
                  <EllipsisTooltipCell truncate={shouldTruncate}>{params.value}</EllipsisTooltipCell>
                ),
          } as typeof wrappedColumn;
        } else if (col.type !== "actions" && col.type !== "singleSelect") {
          // For columns without renderCell (but not actions or singleSelect), add default renderCell with wrapper
          wrappedColumn = {
            ...wrappedColumn,
            renderCell: (params: GridRenderCellParams) => (
              <EllipsisTooltipCell truncate={shouldTruncate}>{params.value}</EllipsisTooltipCell>
            ),
          } as typeof wrappedColumn;
        }

        if (wrappedColumn.remoteFilter?.filterOperators)
          return {
            ...wrappedColumn,
            filterOperators: wrappedColumn.remoteFilter.filterOperators,
          };
        return wrappedColumn;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(depedancyArray ?? []), columns]);
  };
